import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";

import { useQuery } from "@apollo/client";
import moment from "moment";

import { BORDER_COLOR, TEXT_MUTED, SummaryCard } from "./Shared.component";
import Theme from "../utils/Theme";
import { REFERRALS_REPORT } from "../graphql/query/query";

// Header background (same as Manage User table header)
const HEADER_BG = "#F4F6F8";

// Card shadow used by the Manage User table
const PAPER_SHADOW =
  "0px 0px 2px 0px rgba(0, 171, 85, 0.2),0px 12px 24px -4px rgba(0, 171, 85, 0.12)";

// -----------------------------------------------------------------------------
// Columns
//
// Referrals are split into two columns (name / phone). Delivery Referral and
// Follow-Up Referral are condensed to Yes/No, and Follow-Up Date / # Referrals
// are narrower. Headers are allowed to wrap onto two lines to save width.
// -----------------------------------------------------------------------------
const COLUMNS = [
  { key: "purchaseDate", label: "Purchase Date", width: 88 },
  { key: "customer", label: "Customer", width: 120 },
  { key: "cell", label: "Cell", width: 115 },
  { key: "email", label: "Email", width: 170 },
  { key: "sales", label: "Sales", width: 100 },
  { key: "deliveryReferral", label: "Delivery Referral", width: 72, align: "center" },
  { key: "followUpDate", label: "Follow-Up Date", width: 88 },
  { key: "followUpReferral", label: "Follow-Up Referral", width: 76, align: "center" },
  { key: "referralCount", label: "# Refs", width: 54, align: "center" },
  { key: "referralName", label: "Referral Name", width: 160 },
  { key: "referralPhone", label: "Referral Phone", width: 120 },
];

const TABLE_MIN_WIDTH = COLUMNS.reduce((sum, c) => sum + c.width, 0);

const formatDate = (value) => (value ? moment(value).format("MM/DD/YYYY") : "");
const formatRate = (value) =>
  typeof value === "number" ? `${(value * 100).toFixed(1)}%` : "—";

// Character truncation keeps a stray long token (e.g. an email with no spaces)
// from ever stretching a fixed-width column. Full text is available on hover.
const truncate = (value, maxLength) => {
  if (!value) return "—";
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};

const CELL_MAX_CHARS = {
  customer: 14,
  cell: 15,
  email: 24,
  sales: 12,
  referralName: 22,
  referralPhone: 16,
};

const widthOf = (key) => COLUMNS.find((c) => c.key === key).width;

const truncatedCellSx = (key, extraSx = {}) => ({
  fontSize: "13px",
  width: widthOf(key),
  maxWidth: widthOf(key),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...extraSx,
});

// -----------------------------------------------------------------------------
// Yes / No indicator
//
// Strict: only an explicit "Provided" counts as Yes, so unexpected backend
// values can never silently become "Yes".
// "N/A" (follow-up not due yet) renders as a dash.
// -----------------------------------------------------------------------------
const isYes = (value) =>
  value === true ||
  (typeof value === "string" && value.trim().toLowerCase() === "provided");

const isNotApplicable = (value) =>
  typeof value === "string" && value.trim().toLowerCase() === "n/a";

const YesNo = ({ value }) => {
  // Follow-up not due yet: show a dash instead of Yes/No
  if (isNotApplicable(value)) {
    return (
      <Typography
        component="span"
        title="Follow-up not due yet"
        sx={{ fontSize: "13px", color: TEXT_MUTED }}
      >
        —
      </Typography>
    );
  }

  const yes = isYes(value);
  return (
    <Typography
      component="span"
      title={typeof value === "string" ? value : undefined}
      sx={{
        fontSize: "12px",
        fontWeight: 600,
        px: "8px",
        py: "2px",
        borderRadius: "10px",
        color: yes ? "#1b5e20" : TEXT_MUTED,
        backgroundColor: yes ? "#e8f5e9" : "#f1f1f1",
      }}
    >
      {yes ? "Yes" : "No"}
    </Typography>
  );
};

// Shared line style so the name and phone columns stay aligned row-by-row.
const referralLineSx = {
  fontSize: "13px",
  lineHeight: "20px",
  height: "20px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ReferralLines = ({ items, renderText, fullText }) => {
  if (!items || items.length === 0) {
    return (
      <Typography component="span" sx={{ fontSize: "13px", color: TEXT_MUTED }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {items.map((r, i) => (
        <Typography key={i} component="div" title={fullText(r)} sx={referralLineSx}>
          {renderText(r)}
        </Typography>
      ))}
    </Box>
  );
};

const ReferralsTable = ({
  dealershipId,
  employeeUserId,
  isEmployeeOnly,
  dateRange,
  timezone,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, loading, error } = useQuery(REFERRALS_REPORT, {
    skip: !dealershipId,
    fetchPolicy: "network-only",
    variables: {
      reportInput: {
        dealership_id: dealershipId,
        employee_user_id: employeeUserId,
        isEmployeeOnly: !!isEmployeeOnly,
        f_startDate: dateRange?.startDate,
        f_endDate: dateRange?.endDate,
        timezone,
        pageCount: page + 1,
        rowsPerPage,
      },
    },
  });

  const report = data?.referralsReport;
  const apiErrors = report?.errors;
  const summaryData = report?.summary;
  const rows = report?.rows || [];
  const total = report?.total || 0;

  const summary = summaryData
    ? [
        { label: "Deliveries", value: String(summaryData.deliveries ?? 0) },
        { label: "Referrals Received (At Delivery)", value: String(summaryData.referralsAtDelivery ?? 0) },
        { label: "Referrals Received (Follow Up)", value: String(summaryData.referralsFollowUp ?? 0) },
        { label: "Total Referrals", value: String(summaryData.totalReferrals ?? 0) },
        { label: "Referral Rate", value: formatRate(summaryData.referralRate) },
      ]
    : [];

  return (
    <>
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: 2 }}>
        {summary.map((s) => (
          <SummaryCard key={s.label} label={s.label} value={s.value} />
        ))}
      </Box>

      {(error || apiErrors?.length > 0) && (
        <Typography sx={{ fontSize: "13px", color: "error.main", mb: 1 }}>
          {error?.message || apiErrors?.[0]?.message || "Unable to load Referrals report."}
        </Typography>
      )}

      {/* Table — same card look as the Manage User table */}
      <Paper
        elevation={0}
        sx={{
          boxShadow: PAPER_SHADOW,
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          sx={{
            // Fixed height: pagination stays in the same place whether there
            // are 1 or 100 rows. Rows scroll inside when they don't fit.
            height: "calc(100vh - 430px)",
            minHeight: "250px",
            overflowY: "auto",
            overflowX: "auto",
            "& .MuiTableCell-root": {
              padding: "8px 10px",
              verticalAlign: "top",
              textAlign: "center",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH }}>
            <colgroup>
              {COLUMNS.map((col) => (
                <col key={col.key} style={{ width: col.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      lineHeight: 1.2,
                      color: "text.primary",
                      backgroundColor: HEADER_BG,
                      // Headers wrap onto multiple lines instead of forcing width.
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      verticalAlign: "bottom",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4, color: TEXT_MUTED, fontSize: "13px" }}>
                    No Referrals records found for this range.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow hover key={idx}>
                    <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {formatDate(row.purchaseDate)}
                    </TableCell>

                    <TableCell
                      title={row.customer || ""}
                      sx={truncatedCellSx("customer", {
                        color: Theme.palette.primary.main,
                        fontWeight: 600,
                      })}
                    >
                      {truncate(row.customer, CELL_MAX_CHARS.customer)}
                    </TableCell>

                    <TableCell title={row.cell || ""} sx={truncatedCellSx("cell")}>
                      {truncate(row.cell, CELL_MAX_CHARS.cell)}
                    </TableCell>

                    <TableCell title={row.email || ""} sx={truncatedCellSx("email")}>
                      {truncate(row.email, CELL_MAX_CHARS.email)}
                    </TableCell>

                    <TableCell title={row.sales || ""} sx={truncatedCellSx("sales")}>
                      {truncate(row.sales, CELL_MAX_CHARS.sales)}
                    </TableCell>

                    <TableCell align="center"><YesNo value={row.deliveryReferral} /></TableCell>

                    <TableCell sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {formatDate(row.followUpDate) || "—"}
                    </TableCell>

                    <TableCell align="center"><YesNo value={row.followUpReferral} /></TableCell>

                    <TableCell align="center" sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {row.referralCount ?? 0}
                    </TableCell>

                    {/* Referral name — one line per referral */}
                    <TableCell sx={{ width: widthOf("referralName"), maxWidth: widthOf("referralName") }}>
                      <ReferralLines
                        items={row.referrals}
                        fullText={(r) => r.name || "Unknown"}
                        renderText={(r) => truncate(r.name || "Unknown", CELL_MAX_CHARS.referralName)}
                      />
                    </TableCell>

                    {/* Referral phone — same line height, so it lines up with the name */}
                    <TableCell sx={{ width: widthOf("referralPhone"), maxWidth: widthOf("referralPhone") }}>
                      <ReferralLines
                        items={row.referrals}
                        fullText={(r) => r.mobile_number || ""}
                        renderText={(r) => truncate(r.mobile_number, CELL_MAX_CHARS.referralPhone)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", padding: "0 8px", borderTop: `1px solid ${BORDER_COLOR}`, backgroundColor: "#fff", "& .MuiTablePagination-toolbar": { minHeight: "40px", padding: 0 } }}>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default ReferralsTable;